// src/components/layouts/ClientPortalLayout.js
import React, { useState } from 'react';
import { Outlet, useNavigate, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileSignature,
  MessageCircle,
  Settings,
  LogOut,
  MoreVertical,
  Receipt,
  Briefcase,
  Menu,
  X
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-toastify';
import { useHeader } from '../context/HeaderContext';
import { authStorage } from '../functions/auth';

const ClientPortalLayout = () => {
  const { logout, getUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const user = authStorage.getUserData() || {};
  const { extraHeaderContent } = useHeader();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const mainNavLinks = [
    { path: '/client-portal-dash', icon: FileSignature, text: 'Documents' },
    { path: '/client-portal-services', icon: Briefcase, text: 'Services' },
    { path: '/client-invoices', icon: Receipt, text: 'Invoices' }
  ];

  const dropdownNavLinks = [
    // L26: Support page hidden from the client portal for launch (kept for later
    // use once real contact info and SLA terms are wired). Re-enable by
    // uncommenting this link and the /support route in App.js.
    // { path: '/support', icon: MessageCircle, text: 'Support' },
    { path: '/client-portal-settings', icon: Settings, text: 'Settings' }
  ];

  const isActive = (path) => location.pathname === path;

  const initials = (user?.name || 'G T')
    .split(' ')
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const handleLogout = async () => {
    try {
      await logout.mutateAsync();
      toast.success('Successfully logged out');
    navigate('/client-portal-login', { replace: true }); // Use replace to prevent back navigation
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Error during logout');
      navigate('/client-portal-login', { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-grayteck-50 dark:bg-darkbg-900">
      {/* App Header */}
      <header className="sticky top-0 z-30 bg-white/95 dark:bg-darkbg-800/95 backdrop-blur border-b border-grayteck-200 dark:border-darkbg-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="sm:hidden p-2 -ml-2 text-grayteck-600 hover:text-grayteck-900 dark:text-grayteck-400 dark:hover:text-grayteck-100 rounded-md"
              aria-label="Toggle navigation menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* Brand wordmark */}
            <div className="flex items-center gap-3 sm:gap-10 min-w-0">
              <NavLink to="/client-portal-dash" className="flex items-baseline gap-2 shrink-0">
                <span className="font-display text-lg font-bold tracking-tight text-grayteck-900 dark:text-grayteck-100">
                  GrayTeck
                  <span className="text-gold-400">.</span>
                </span>
                <span className="hidden md:inline text-[11px] font-semibold uppercase tracking-caps text-grayteck-600 dark:text-grayteck-400">
                  Client Portal
                </span>
              </NavLink>

              {/* Primary navigation (desktop) */}
              <nav className="hidden sm:flex items-center gap-1">
                {mainNavLinks.map((link) => {
                  const Icon = link.icon;
                  const active = isActive(link.path);
                  return (
                    <NavLink
                      key={link.path}
                      to={link.path}
                      className={`relative flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                        active
                          ? 'text-grayteck-900 dark:text-grayteck-100'
                          : 'text-grayteck-700 dark:text-grayteck-400 hover:text-grayteck-900 dark:hover:text-grayteck-100 hover:bg-grayteck-100 dark:hover:bg-darkbg-700'
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${active ? 'text-grayteck-800 dark:text-gold-400' : ''}`} />
                      {link.text}
                      {active && (
                        <span className="absolute -bottom-[13px] left-3 right-3 h-0.5 rounded-full bg-gold-400" />
                      )}
                    </NavLink>
                  );
                })}
              </nav>
            </div>

            {/* Right section */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Injected Extra Header Content */}
              <div className="hidden sm:block">
                {extraHeaderContent}
              </div>

              {/* User chip */}
              <div className="hidden sm:flex items-center gap-2.5 pl-3 border-l border-grayteck-200 dark:border-darkbg-600">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-grayteck-800 dark:bg-grayteck-700 text-white text-xs font-bold tracking-wide">
                  {initials}
                </span>
                <span className="text-sm font-semibold text-grayteck-800 dark:text-grayteck-200 max-w-[140px] truncate">
                  {user?.name || 'Guest'}
                </span>
              </div>

              {/* Three Dot Menu */}
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="p-2 text-grayteck-600 hover:text-grayteck-900 dark:text-grayteck-400 dark:hover:text-grayteck-100 rounded-md hover:bg-grayteck-100 dark:hover:bg-darkbg-700 transition-colors"
                  aria-label="More options"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-darkbg-700 rounded-lg shadow-card-lg dark:shadow-card-dark border border-grayteck-200 dark:border-darkbg-600 py-1.5 z-10 overflow-hidden">
                    {dropdownNavLinks.map((link) => {
                      const Icon = link.icon;
                      return (
                        <NavLink
                          key={link.path}
                          to={link.path}
                          onClick={() => setIsDropdownOpen(false)}
                          className={({ isActive }) => `
                            flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors
                            ${isActive
                              ? 'text-grayteck-900 dark:text-grayteck-100 bg-grayteck-100 dark:bg-darkbg-600'
                              : 'text-grayteck-600 dark:text-grayteck-300 hover:bg-grayteck-50 dark:hover:bg-darkbg-600 hover:text-grayteck-900 dark:hover:text-grayteck-100'
                            }
                          `}
                        >
                          <Icon className="w-4 h-4" />
                          {link.text}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Logout Button */}
            <button
              onClick={handleLogout}
                className="flex items-center px-2 py-2 sm:px-3.5 text-sm font-semibold text-grayteck-700 hover:text-grayteck-900 dark:text-grayteck-300 dark:hover:text-grayteck-100 border border-grayteck-300 dark:border-darkbg-500 hover:border-grayteck-400 dark:hover:border-grayteck-600 bg-transparent hover:bg-grayteck-100 dark:hover:bg-darkbg-700 rounded-md transition-all duration-200"
            >
                <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <AnimatePresence initial={false}>
          {isMobileMenuOpen && (
            <motion.div
              key="mobile-nav"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              className="sm:hidden overflow-hidden border-t border-grayteck-200 dark:border-darkbg-600 bg-white dark:bg-darkbg-800"
            >
              <div className="px-4 py-3 space-y-1">
                {mainNavLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <NavLink
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-2.5 py-2.5 px-3 rounded-md text-sm font-semibold transition-colors ${
                        isActive(link.path)
                          ? 'text-grayteck-900 dark:text-grayteck-100 bg-grayteck-100 dark:bg-darkbg-700 border-l-2 border-gold-400'
                          : 'text-grayteck-700 dark:text-grayteck-400 hover:text-grayteck-900 dark:hover:text-grayteck-100 hover:bg-grayteck-50 dark:hover:bg-darkbg-700'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {link.text}
                    </NavLink>
                  );
                })}
                {/* Show extra header content on mobile */}
                {extraHeaderContent && (
                  <div className="pt-2 border-t border-grayteck-200 dark:border-darkbg-600">
                    {extraHeaderContent}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full max-w-7xl mx-auto py-6 px-4 sm:py-10 sm:px-6 lg:px-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-grayteck-200 dark:border-darkbg-600">
        <div className="max-w-7xl mx-auto py-5 px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span className="font-display text-sm font-bold text-grayteck-700 dark:text-grayteck-300">
            GrayTeck<span className="text-gold-400">.</span>
          </span>
          <p className="text-xs text-grayteck-600 dark:text-grayteck-400">
            © {new Date().getFullYear()} GrayTecknologies LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ClientPortalLayout;
